import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, PlusCircle, Settings, LogOut, Loader2, Menu, Zap, Gift, Rocket } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Portfolio", icon: LayoutGrid, testId: "nav-portfolio" },
  { to: "/enrollments", label: "Enrollments", icon: Zap, testId: "nav-enrollments" },
  { to: "/new", label: "New Audit", icon: PlusCircle, testId: "nav-new-audit" },
  { to: "/addons", label: "Add-ons", icon: Gift, testId: "nav-addons", staffOnly: true },
  { to: "/golive", label: "Go-live", icon: Rocket, testId: "nav-golive", staffOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

function SidebarContent({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border px-6 py-5">
        <img src="/icon-192.png" alt="" className="h-8 w-8" />
        <span className="font-display text-lg font-semibold tracking-tight">LocalPro Beacon</span>
      </div>

      <nav className="flex-1 space-y-1 p-3">
        {NAV.filter((i) => !i.staffOnly || ["owner", "admin"].includes(user?.role)).map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-border p-3">
        <div className="mb-2 flex items-center justify-between px-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <ThemeToggle />
        </div>
        <button
          data-testid="logout-button"
          onClick={() => { logout(); navigate("/login"); }}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

export function AppLayout({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card md:flex">
        <SidebarContent />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="" className="h-7 w-7" />
            <span className="font-display text-base font-semibold tracking-tight">LocalPro Beacon</span>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open menu"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground hover:bg-accent"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-border bg-card p-0" data-testid="mobile-nav-sheet">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <div className="mx-auto w-full max-w-7xl px-5 py-8 md:px-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
